<template>
    <div class="container">
      <h1 class="title">MT数据展板</h1>

      <div class="upload-container">
        <div
            class="upload-area"
            :class="{ dragover: isDragover }"
            @click="triggerFileInput"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
        >
          <div class="upload-icon">📁</div>
          <input
              ref="fileInput"
              type="file"
              accept=".xls"
              class="file-input"
              @change="handleFileChange"
              style="display: none"
          />
        </div>
        <button
            class="upload-btn"
            @click="uploadFile"
            :disabled="!selectedFile || isUploading"
            :title="selectedFile ? '上传: ' + selectedFile.name : '请先选择文件'"
        >
          <span v-if="isUploading" class="loading"></span>
          {{ isUploading ? '上传中' : '上传' }}
        </button>
        <div v-if="selectedFile" class="file-info">
          {{ selectedFile.name }}
        </div>
        <div v-if="errorMessage" class="error-message">
          ❌ {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="success-message">
          ✅ {{ successMessage }}
        </div>
      </div>

      <div v-if="flattenedData && Object.keys(flattenedData).length > 0" class="mt-table-container">
        <div class="table-header">
          <div class="header-row">
            <div class="col-serial">序号</div>
            <div class="col-model">型号</div>
            <div class="col-type">类型</div>
            <div class="col-timeline-header">
              <div class="time-scale" ref="timeScaleRef">
                <div v-for="(day, idx) in timeScaleDays" :key="idx" class="time-scale-day" :style="{ left: day.left + '%' }">
                  {{ day.label }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="table-body">
          <div v-for="(types, machineName, groupIndex) in flattenedData" :key="machineName" class="machine-group">
            <div v-for="(typeData, typeIndex) in types" :key="typeData.type" class="data-row">
              <div class="col-serial" v-if="typeIndex === 0">
                <div class="merged-cell">{{ groupIndex + 1 }}</div>
              </div>
              <div v-else class="col-serial"></div>
              <div class="col-model" v-if="typeIndex === 0">
                <div class="merged-cell">{{ machineName }}</div>
              </div>
              <div v-else class="col-model"></div>
              <div class="col-type" :class="'type-' + typeData.type.toLowerCase()">{{ typeData.type }}</div>
              <div class="col-timeline">
                <div class="timeline-container">
                  <div 
                    v-for="(segment, segIndex) in typeData.segments" 
                    :key="segIndex"
                    class="timeline-segment"
                    :class="'segment-' + typeData.type.toLowerCase()"
                    :style="calculateSegmentStyle(segment)"
                    :title="`${segment.componentName || ''}\n${formatDate(segment.startTime, 'MM-dd HH:mm')} ~ ${formatDate(segment.endTime, 'MM-dd HH:mm')}\n${typeData.type}: ${segment.value}小时`"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="legend">
          <div class="legend-item">
            <span class="legend-color mt0-color"></span>
            <span>MT0 (蓝色)</span>
          </div>
          <div class="legend-item">
            <span class="legend-color mt1-color"></span>
            <span>MT1 (绿色)</span>
          </div>
          <div class="legend-item">
            <span class="legend-color mt2-color"></span>
            <span>MT2 (黄色)</span>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import {post, formatDate} from "@/apis/restUtils";
export default {
  name: 'machinetoolMt',
  data() {
    return {
      selectedFile: null,
      isDragover: false,
      isUploading: false,
      mtData: [],
      errorMessage: '',
      successMessage: '',
      timeScaleDays: [],
      minTimestamp: null,
      maxTimestamp: null
    }
  },
  computed: {
    groupedData() {
      if (!this.mtData || this.mtData.length === 0) return {};
      
      const groups = {};
      this.mtData.forEach(item => {
        const name = item.machinetoolName || '未命名';
        if (!groups[name]) {
          groups[name] = [];
        }
        groups[name].push(item);
      });
      
      Object.keys(groups).forEach(name => {
        groups[name].sort((a, b) => {
          const timeA = new Date(a.processTime).getTime();
          const timeB = new Date(b.processTime).getTime();
          return timeA - timeB;
        });
      });
      
      return groups;
    },
    flattenedData() {
      if (!this.groupedData || Object.keys(this.groupedData).length === 0) {
        return {};
      }
      
      const result = {};
      
      Object.keys(this.groupedData).forEach(machineName => {
        const items = this.groupedData[machineName];
        const mt0Segments = [];
        const mt1Segments = [];
        const mt2Segments = [];
        
        items.forEach((item) => {
          // MT0 和 MT1 基于 processTime
          if (item.MT0 && item.MT0 > 0 && item.processTime) {
            const startTime = new Date(item.processTime).getTime();
            const endTime = startTime + item.MT0 * 60 * 60 * 1000;
            mt0Segments.push({
              ...item,
              type: 'MT0',
              startTime: new Date(startTime),
              endTime: new Date(endTime),
              value: item.MT0
            });
          }
          
          if (item.MT1 && item.MT1 > 0 && item.processTime) {
            const startTime = new Date(item.processTime).getTime();
            const endTime = startTime + item.MT1 * 60 * 60 * 1000;
            mt1Segments.push({
              ...item,
              type: 'MT1',
              startTime: new Date(startTime),
              endTime: new Date(endTime),
              value: item.MT1
            });
          }
          
          // MT2 基于 actualProcessTime
          if (item.MT2 && item.MT2 > 0 && item.actualProcessTime) {
            const startTime = new Date(item.actualProcessTime).getTime();
            const endTime = startTime + item.MT2 * 60 * 60 * 1000;
            mt2Segments.push({
              ...item,
              type: 'MT2',
              startTime: new Date(startTime),
              endTime: new Date(endTime),
              value: item.MT2
            });
          }
        });
        
        // 每个机床只有3行：MT0、MT1、MT2
        result[machineName] = [
          { type: 'MT0', segments: mt0Segments },
          { type: 'MT1', segments: mt1Segments },
          { type: 'MT2', segments: mt2Segments }
        ];
      });
      
      return result;
    }
  },
  mounted(){
    post('/organization/loadAllMT', {}, response=>{
      this.responseHandler(response);
    });
  },
  methods: {
    formatDate(date, fmt) {
      if (!date) return '';
      return formatDate(date, fmt);
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectFile(file)
      }
    },
    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragover = true;
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.dropEffect = 'copy';
    },
    handleDragEnter(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragover = true;
    },
    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragover = false;
      }
    },
    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragover = false;
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        this.selectFile(files[0]);
      }
    },
    selectFile(file) {
      if (!file.name.toLowerCase().endsWith('.xls')) {
        this.errorMessage = '请选择 .xls 格式的Excel文件'
        this.selectedFile = null
        return
      }
      this.selectedFile = file
      this.errorMessage = ''
      this.successMessage = ''
    },
    responseHandler(response){
      if (response.data.success) {
        this.mtData = response.data.data
        this.successMessage = '加载成功！'
        this.calculateTimeRange();
      } else {
        this.errorMessage = response.data.message || '加载失败'
      }
    },
    async uploadFile() {
      if (!this.selectedFile || this.isUploading) return
      this.isUploading = true
      this.errorMessage = ''
      this.successMessage = ''

      const formData = new FormData()
      formData.append('file', this.selectedFile)

      try {
         await post('/organization/mtexcelupload', formData, response=>{
           this.responseHandler(response);
        });
      } catch (error) {
        this.errorMessage = '上传失败，请检查后端服务是否启动'
      } finally {
        this.isUploading = false
      }
    },
    calculateTimeRange() {
      if (!this.mtData || this.mtData.length === 0) return;

      let minTime = null;
      let maxTime = null;

      this.mtData.forEach((item) => {
        if (item.processTime) {
          const pt = new Date(item.processTime).getTime();
          if (!minTime || pt < minTime) minTime = pt;
          
          if (item.MT0 && item.MT0 > 0) {
            const mt0End = pt + item.MT0 * 60 * 60 * 1000;
            if (!maxTime || mt0End > maxTime) maxTime = mt0End;
          }
          
          if (item.MT1 && item.MT1 > 0) {
            const mt1End = pt + item.MT1 * 60 * 60 * 1000;
            if (!maxTime || mt1End > maxTime) maxTime = mt1End;
          }
        }
        
        if (item.actualProcessTime) {
          const apt = new Date(item.actualProcessTime).getTime();
          if (!minTime || apt < minTime) minTime = apt;
          
          if (item.MT2 && item.MT2 > 0) {
            const mt2End = apt + item.MT2 * 60 * 60 * 1000;
            if (!maxTime || mt2End > maxTime) maxTime = mt2End;
          }
        }
      });
      
      if (minTime && maxTime) {
        this.minTimestamp = minTime;
        this.maxTimestamp = maxTime;
        this.generateTimeScale();
      }
    },
    generateTimeScale() {
      if (!this.minTimestamp || !this.maxTimestamp) return;

      const days = [];
      
      const startDate = new Date(this.minTimestamp);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(this.maxTimestamp);
      endDate.setHours(23, 59, 59, 999);
      
      const totalRange = endDate.getTime() - startDate.getTime();
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dayStart = currentDate.getTime();
        const relativeLeft = ((dayStart - startDate.getTime()) / totalRange) * 100;
        
        days.push({
          label: `${currentDate.getMonth() + 1}/${currentDate.getDate()}`,
          left: relativeLeft
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      this.timeScaleDays = days;
      this.adjustedMinTimestamp = startDate.getTime();
      this.adjustedMaxTimestamp = endDate.getTime();
      this.totalRange = totalRange;
    },
    calculateSegmentStyle(item) {
      if (!this.adjustedMinTimestamp || !this.totalRange || !item.startTime || !item.endTime) {
        return {};
      }

      const startTime = new Date(item.startTime).getTime();
      const endTime = new Date(item.endTime).getTime();
      
      const left = ((startTime - this.adjustedMinTimestamp) / this.totalRange) * 100;
      const width = ((endTime - startTime) / this.totalRange) * 100;
      
      let color = '#5470c6';
      if (item.type === 'MT1') {
        color = '#91cc75';
      } else if (item.type === 'MT2') {
        color = '#fac858';
      }
      
      const style = {
        left: `${Math.max(0, left)}%`,
        width: `${Math.max(0.5, width)}%`,
        backgroundColor: color
      };
      
      return style;
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 1800px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 20px;
  min-height: 100vh;
}
.title {
  text-align: center;
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
}
.upload-container{
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}
.upload-area {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.upload-area:hover {
  border-color: #409eff;
  background-color: #f0f7ff;
}
.upload-area.dragover {
  border-color: #409eff;
  background-color: #e6f7ff;
}
.upload-icon {
  font-size: 24px;
  color: #666;
}
.upload-btn {
  padding: 6px 16px;
  background-color: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}
.upload-btn:hover:not(:disabled) {
  background-color: #67b8ff;
  transform: translateY(-1px);
}
.upload-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.file-info {
  padding: 4px 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 11px;
  color: #666;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.loading {
  display: inline-block;
  margin-right: 8px;
  width: 14px;
  height: 14px;
  border: 2px solid #409eff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.error-message {
  margin-top: 12px;
  padding: 10px;
  background-color: #fef0f0;
  border-radius: 4px;
  font-size: 12px;
  color: #f56c6c;
}
.success-message {
  margin-top: 12px;
  padding: 10px;
  background-color: #f0f9eb;
  border-radius: 4px;
  font-size: 12px;
  color: #67c23a;
}
.mt-table-container {
  overflow-x: auto;
}
.table-header {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
}
.header-row {
  display: flex;
  border-bottom: 2px solid #e4e7ed;
  background: #f5f7fa;
}
.data-row {
  display: flex;
  border-bottom: 1px solid #ebf2f9;
  transition: background-color 0.2s;
  height: 28px;
}
.machine-group {
  position: relative;
}
.machine-group:hover .data-row {
  background-color: #fafafa;
}
.col-serial {
  width: 50px;
  min-width: 50px;
  padding: 0;
  text-align: center;
  border-right: 1px solid #e4e7ed;
  font-size: 11px;
  color: #666;
  position: relative;
  height: 100%;
  flex-shrink: 0;
}
.col-serial .merged-cell {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: calc(3 * 28px); /* 3行高度 */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  z-index: 1;
}
.col-model {
  width: 150px;
  min-width: 150px;
  padding: 0;
  border-right: 1px solid #e4e7ed;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  position: relative;
  height: 100%;
  flex-shrink: 0;
}
.col-model .merged-cell {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: calc(3 * 28px); /* 3行高度 */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  z-index: 1;
}
.col-type {
  width: 50px;
  min-width: 50px;
  padding: 5px;
  border-right: 1px solid #e4e7ed;
  text-align: center;
  font-size: 11px;
  font-weight: bold;
}
.type-mt0 { color: #5470c6; }
.type-mt1 { color: #91cc75; }
.type-mt2 { color: #fac858; }
.col-timeline-header {
  flex: 1;
  padding: 0;
  border-bottom: 1px solid #e4e7ed;
}
.col-timeline {
  flex: 1;
  padding: 3px 0;
  min-width: 400px;
}
.time-scale {
  position: relative;
  height: 30px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}
.time-scale-day {
  position: absolute;
  top: 6px;
  font-size: 11px;
  color: #666;
  padding-left: 8px;
  white-space: nowrap;
}
.timeline-container {
  position: relative;
  height: 100%;
  background: repeating-linear-gradient(
    to right,
    #f0f0f0 0px,
    #f0f0f0 1px,
    transparent 1px,
    transparent calc(100% / 31)
  );
  border-radius: 3px;
  overflow: hidden;
}
.timeline-segment {
  position: absolute;
  top: 3px;
  height: 22px;
  border-radius: 2px;
  opacity: 0.85;
  transition: opacity 0.2s;
}
.timeline-segment:hover {
  opacity: 1;
}
.legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 20px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 8px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}
.legend-color {
  width: 20px;
  height: 14px;
  border-radius: 3px;
}
.mt0-color { background-color: #5470c6; }
.mt1-color { background-color: #91cc75; }
.mt2-color { background-color: #fac858; }
.segment-mt0 { background-color: #5470c6; }
.segment-mt1 { background-color: #91cc75; }
.segment-mt2 { background-color: #fac858; }
</style>
